import React, { useState, useEffect } from "react";
import { Row, Col, Upload, Button, Typography, Divider, message } from "antd";
import { SketchPicker } from "react-color";
import { UploadOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../api/api";

const { Title, Link, Paragraph } = Typography;

const Appearance = () => {
  const [primaryColor, setPrimaryColor] = useState("#007bff");
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const agencyCode = userInfo.agency_code;
  const effectiveAgencyCode =
    agencyCode && agencyCode.trim() !== "" ? agencyCode : "agent1";

  const fetchAppearanceSetting = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}appearanceSetting&agency_code=${effectiveAgencyCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const json = await res.json();
      if (json.success && json.data) {
        setPrimaryColor(json.data.theme_color || "#007bff");
        setLogoUrl(json.data.logo_url || null);
        setFaviconUrl(json.data.favicon || null);
      }
    } catch (err) {
      console.error("Failed to fetch appearance settings:", err);
      message.error("Failed to load appearance settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (fileSetter, previewSetter) => (info) => {
    const file = info.file;

    if (file instanceof File) {
      previewSetter(URL.createObjectURL(file));
      fileSetter(file);
    } else {
      console.warn("Invalid file selected", info);
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      formData.append("agency_code", effectiveAgencyCode);

      formData.append("theme_color", primaryColor);

      // Only append files if available
      if (logoFile) {
        formData.append("logo", logoFile); // field name "logo"
      }

      if (faviconFile) {
        formData.append("favicon", faviconFile); // field name "favicon"
      }

      const res = await fetch(`${API_BASE_URL}appearanceSetting`, {
        method: "POST", // or "PUT" depending on your backend
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (json.success) {
        message.success("Appearance settings updated successfully!");
        setTimeout(() => {
          window.location.reload(); // ✅ force reload after short delay
        }, 1000); // small delay so the user sees the message
      } else {
        message.error("Failed to update settings.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      message.error("Something went wrong.");
    }
  };

  useEffect(() => {
    fetchAppearanceSetting();
  }, []);

  return (
    <div>
      <Title level={2}>Appearance Settings</Title>

      <Row gutter={32}>
        <Col span={12}>
          <Title level={4}>1. Pick Primary Color</Title>
          <SketchPicker
            color={primaryColor}
            onChangeComplete={(color) => setPrimaryColor(color.hex)}
          />
          <div style={{ marginTop: 8 }}>
            Selected color: <strong>{primaryColor}</strong>
          </div>
        </Col>

        <Col span={12}>
          <div style={{ marginBottom: 32 }}>
            <Title level={4}>2. Upload Company Logo</Title>
            <Upload
              showUploadList={false}
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
              onChange={handleImageUpload(setLogoFile, setLogoUrl)}
            >
              <Button icon={<UploadOutlined />}>Upload Logo</Button>
            </Upload>
            {logoUrl && (
              <div style={{ marginTop: 12 }}>
                <img
                  src={logoUrl}
                  alt="Logo Preview"
                  style={{ maxHeight: 80 }}
                />
              </div>
            )}
          </div>

          <div>
            <Title level={4}>3. Upload Favicon</Title>

            <Upload
              showUploadList={false}
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
              onChange={handleImageUpload(setFaviconFile, setFaviconUrl)}
            >
              <Button icon={<UploadOutlined />}>Upload Favicon</Button>
            </Upload>
            {faviconUrl && (
              <div style={{ marginTop: 12 }}>
                <img
                  src={faviconUrl}
                  alt="Favicon Preview"
                  style={{ width: 32, height: 32 }}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <Button type="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </div>

      <Divider style={{ margin: "40px 0" }} />
      <Title level={4}>
        Check your whitelabel site here:{" "}
        <Link
          href={`https://${effectiveAgencyCode}.sunbook.ca`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {`https://${effectiveAgencyCode}.sunbook.ca`}
        </Link>
      </Title>
      <div style={{ marginTop: 40 }}>
        <Title level={4}>Check your widget code here:</Title>
        <Paragraph copyable>
          {`<iframe
  src="https://widgets.sunbook.ca/widget?agent=${effectiveAgencyCode}"
  style="width: 100%; height: 350px; border: none; overflow: hidden;"
  scrolling="no"
  loading="lazy"
  title="Sunbook Search Widget">
</iframe>`}
        </Paragraph>
      </div>
      <Divider style={{ margin: "40px 0" }} />

      <div>
        <Title level={4}>Preview Widget</Title>
        <iframe
          src={`https://widgets.sunbook.ca/widget?agent=${effectiveAgencyCode}`}
          style={{
            width: "100%",
            height: 350,
            border: "none",
            overflow: "hidden",
          }}
          scrolling="no"
          loading="lazy"
          title="Sunbook Search Widget"
        />
      </div>
    </div>
  );
};

export default Appearance;
